import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Container, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const Footer: React.FC = () => {
  const appVersion = process.env.REACT_APP_VERSION || "0.0.0";
  const commitSha = process.env.REACT_APP_COMMIT_SHA || "";
  const fallbackCommitShort = appVersion
    .replace(/\D/g, "")
    .padStart(4, "0")
    .slice(-4);
  const commitShort = commitSha ? commitSha.slice(0, 7) : fallbackCommitShort;
  const [copied, setCopied] = useState<{
    key: "version" | "commit";
    token: number;
  } | null>(null);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(null), 1400);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async (key: "version" | "commit", value: string) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        console.debug("[Footer] Copied metadata", { key, value });
        setCopied({ key, token: Date.now() });
        return;
      }
    } catch (error) {
      console.debug("[Footer] Clipboard write failed", { key, error });
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "true");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const succeeded = document.execCommand("copy");
      document.body.removeChild(textarea);
      console.debug("[Footer] Clipboard fallback", {
        key,
        value,
        succeeded,
      });
      if (succeeded) {
        setCopied({ key, token: Date.now() });
      }
    } catch (error) {
      console.debug("[Footer] Clipboard fallback failed", {
        key,
        error,
      });
    }
  };

  const copiedToastSx = {
    position: "absolute",
    right: 6,
    top: -12,
    pointerEvents: "none",
    fontSize: "0.65rem",
    color: "success.main",
    opacity: 0,
    animation: "footerCopyToast 1200ms ease",
    "@keyframes footerCopyToast": {
      "0%": { opacity: 0, transform: "translateY(8px)" },
      "20%": { opacity: 1, transform: "translateY(0)" },
      "70%": { opacity: 1, transform: "translateY(-6px)" },
      "100%": { opacity: 0, transform: "translateY(-10px)" },
    },
  };

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: "auto",
        backgroundColor: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Link
              component={RouterLink}
              to="/terms"
              color="text.secondary"
              underline="hover"
              sx={{ fontSize: "0.875rem" }}
            >
              Terms of Service
            </Link>
            <Link
              component={RouterLink}
              to="/privacy"
              color="text.secondary"
              underline="hover"
              sx={{ fontSize: "0.875rem" }}
            >
              Privacy Policy
            </Link>
            <Link
              href="mailto:support@financetracker.app"
              color="text.secondary"
              underline="hover"
              sx={{ fontSize: "0.875rem" }}
            >
              Contact Support
            </Link>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
            }}
          >
            <ButtonBase
              onClick={() => handleCopy("version", appVersion)}
              aria-label={`Copy app version ${appVersion}`}
              sx={{
                position: "relative",
                borderRadius: 1,
                px: 0.9,
                py: 0.35,
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                fontSize: "0.72rem",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                textTransform: "uppercase",
                transition: "all 150ms ease",
                "&:hover": {
                  borderColor: "text.primary",
                  color: "text.primary",
                },
              }}
            >
              v{appVersion}
              {copied?.key === "version" && (
                <Typography key={copied.token} sx={copiedToastSx}>
                  Copied!
                </Typography>
              )}
            </ButtonBase>
            <ButtonBase
              onClick={() =>
                handleCopy("commit", commitSha ? commitSha : `#${commitShort}`)
              }
              aria-label={`Copy commit #${commitShort}`}
              sx={{
                position: "relative",
                borderRadius: 1,
                px: 0.9,
                py: 0.35,
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                fontSize: "0.72rem",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                textTransform: "uppercase",
                transition: "all 150ms ease",
                "&:hover": {
                  borderColor: "text.primary",
                  color: "text.primary",
                },
              }}
            >
              commit #{commitShort}
              {copied?.key === "commit" && (
                <Typography key={copied.token} sx={copiedToastSx}>
                  Copied!
                </Typography>
              )}
            </ButtonBase>
          </Box>

          <Typography variant="body2" color="text.secondary">
            © {currentYear} Focus: Finance. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
